import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { checkAndSetStats, isMobileUserAgent } from "../helpers/helpers";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";
import { Sidebar } from "primereact/sidebar";
import ContactMeBlock from "../components/ContactMeBlock";

// Hook for scroll-triggered animations
const useScrollAnimation = (threshold = 0.1) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold, rootMargin: "0px 0px -50px 0px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    return [ref, isVisible];
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0, animation = "fadeUp" }) => {
    const [ref, isVisible] = useScrollAnimation();

    const animationClass = animation === "fadeUp"
        ? "scroll-animate"
        : animation === "scale"
        ? "scroll-animate-scale"
        : "scroll-animate";

    return (
        <div
            ref={ref}
            className={`${animationClass} ${isVisible ? "is-visible" : ""} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

// Delivery info card component
const DeliveryCard = ({ icon, title, description, highlight = false, delay = 0 }) => {
    return (
        <AnimatedSection delay={delay}>
            <div className={`rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 border ${
                highlight
                    ? "bg-brand-gold-light border-brand-gold/30 shadow-lg"
                    : "bg-white border-neutral-100 shadow-sm hover:shadow-lg"
            }`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 ${
                    highlight ? "bg-brand-gold" : "bg-brand-gold-light"
                }`}>
                    <span className={`text-2xl ${highlight ? "text-white" : ""}`}>{icon}</span>
                </div>
                <h3 className="text-xl font-medium text-neutral-800 mb-3">{title}</h3>
                <p className="text-neutral-600 leading-relaxed">{description}</p>
            </div>
        </AnimatedSection>
    );
};

// Step component for delivery process
const DeliveryStep = ({ number, title, description, delay = 0 }) => {
    return (
        <AnimatedSection delay={delay}>
            <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-gold flex items-center justify-center text-white font-medium text-lg">
                    {number}
                </div>
                <div>
                    <h3 className="text-lg font-medium text-neutral-800 mb-2">{title}</h3>
                    <p className="text-neutral-500 leading-relaxed">{description}</p>
                </div>
            </div>
        </AnimatedSection>
    );
};

function Delivery() {
    const doesStatsSent = useRef(false);
    const [contactVisible, setContactVisible] = useState(false);
    const isMobile = isMobileUserAgent();

    useLayoutEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        document.body.style.backgroundColor = "#FFFFFF";
    }, []);

    useEffect(() => {
        if (!doesStatsSent.current) {
            checkAndSetStats("deliveryPage");
            doesStatsSent.current = true;
        }
    }, []);

    const novaPoshtaRules = [
        {
            icon: "📦",
            title: "Безкоштовна доставка",
            description: "При замовленні від 50 000 грн доставка по Україні безкоштовна. Для замовлень меншої суми вартість доставки згідно тарифів Нової Пошти.",
        },
        {
            icon: "⏱️",
            title: "Терміни доставки",
            description: "Доставка здійснюється протягом 1-3 робочих днів залежно від вашого міста. Відправка замовлення — в той самий день, або наступного дня після оформлення.",
        },
        {
            icon: "🔒",
            title: "Страхування",
            description: "Всі відправлення застраховані на повну вартість товару. У випадку пошкодження або втрати — повне відшкодування.",
        },
        {
            icon: "💳",
            title: "Оплата при отриманні",
            description: "Можливість оплати накладеним платежем при отриманні на відділенні Нової Пошти або кур'єру.",
        },
    ];

    const deliverySteps = [
        {
            title: "Оформлення замовлення",
            description: "Оберіть товари та оформіть замовлення на сайті або зв'яжіться з менеджером.",
        },
        {
            title: "Підтвердження",
            description: "Наш менеджер зв'яжеться з вами для підтвердження замовлення та уточнення деталей доставки.",
        },
        {
            title: "Відправка",
            description: "Після підтвердження ваше замовлення буде ретельно упаковано та відправлено в той самий день, або наступного дня.",
        },
        {
            title: "Отримання",
            description: "Ви отримаєте повідомлення з номером ТТН. Забирайте замовлення на відділенні або очікуйте кур'єра.",
        },
    ];

    return (
        <div className="bg-white min-h-screen">
            <SEO
                title="Доставка"
                description="Інформація про доставку ювелірних виробів від бренду «Цацки». Доставка Новою Поштою по всій Україні. Безкоштовна доставка при замовленні від 2000 грн."
                url="/delivery"
            />
            <Navigation showBack variant="light" />

            <main role="main">
                {/* Hero Section */}
                <header className="relative min-h-[60vh] lg:min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 overflow-hidden">
                    {/* Animated background elements */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl animate-pulse" />
                        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
                    </div>

                    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
                        <h1 className="opacity-0 animate-fade-in-up text-white text-5xl lg:text-7xl font-light tracking-tight mb-6">
                            Доставка
                            <span className="block text-brand-gold mt-2">по всій Україні</span>
                        </h1>
                        <p className="opacity-0 animate-fade-in-up animate-delay-200 text-white/70 text-xl lg:text-2xl font-light leading-relaxed max-w-2xl mx-auto">
                            Швидка та надійна доставка ювелірних виробів
                        </p>
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in-up animate-delay-400">
                        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-2">
                            <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
                        </div>
                    </div>
                </header>

                {/* Nova Poshta Section */}
                <section className="py-20 lg:py-32 px-6">
                    <div className="max-w-6xl mx-auto">
                        <AnimatedSection className="text-center mb-16">
                            <div className="inline-flex items-center gap-3 mb-4">
                                <span className="text-brand-gold text-sm font-medium tracking-widest uppercase">
                                    Основний спосіб доставки
                                </span>
                            </div>
                            <div className="flex items-center justify-center gap-3 lg:gap-4 mb-4">
                                <svg className="w-8 h-8 lg:w-11 lg:h-11 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 42" fill="none">
                                    <path d="M18.5391 18.0849V12.2662H14.7887L19.3554 7.69359C19.5739 7.47376 19.8337 7.29932 20.1197 7.1803C20.4058 7.06127 20.7125 7 21.0223 7C21.332 7 21.6387 7.06127 21.9248 7.1803C22.2108 7.29932 22.4706 7.47376 22.6891 7.69359L27.2558 12.2662H23.4769V18.0849H18.5391Z" fill="#DA291C"/>
                                    <path d="M12.2599 27.2531V14.7355L7.69317 19.3081C7.47346 19.5274 7.29913 19.788 7.18018 20.0749C7.06123 20.3618 7 20.6694 7 20.98C7 21.2906 7.06123 21.5982 7.18018 21.8851C7.29913 22.172 7.47346 22.4326 7.69317 22.6519L12.2599 27.2531Z" fill="#DA291C"/>
                                    <path d="M23.4483 29.7338V23.9093H18.5106V29.7338H14.7316L19.2983 34.3064C19.5169 34.5262 19.7766 34.7007 20.0627 34.8197C20.3487 34.9387 20.6554 35 20.9652 35C21.2749 35 21.5817 34.9387 21.8677 34.8197C22.1538 34.7007 22.4135 34.5262 22.632 34.3064L27.1987 29.7338H23.4483Z" fill="#DA291C"/>
                                    <path d="M29.7447 14.7526V27.2702L34.3114 22.6976C34.5322 22.4751 34.7067 22.211 34.8249 21.9206C34.9431 21.6301 35.0026 21.3191 34.9999 21.0055C34.9973 20.6918 34.9325 20.3819 34.8094 20.0935C34.6864 19.8051 34.5074 19.544 34.2828 19.3253L29.7447 14.7526Z" fill="#DA291C"/>
                                </svg>
                                <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight">
                                    Нова Пошта
                                </h2>
                            </div>
                            <p className="text-neutral-500 text-lg max-w-2xl mx-auto">
                                Доставка на відділення, поштомат або кур'єром до дверей
                            </p>
                        </AnimatedSection>

                        <div className="grid md:grid-cols-2 gap-8">
                            {novaPoshtaRules.map((rule, index) => (
                                <DeliveryCard
                                    key={index}
                                    icon={rule.icon}
                                    title={rule.title}
                                    description={rule.description}
                                    delay={index * 100}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Other Delivery Services Section */}
                <section className="py-20 lg:py-32 bg-neutral-50 px-6">
                    <div className="max-w-4xl mx-auto">
                        <AnimatedSection className="text-center mb-12">
                            <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                Альтернативні способи
                            </span>
                            <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight mb-4">
                                Інші служби доставки
                            </h2>
                        </AnimatedSection>

                        <AnimatedSection delay={100}>
                            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-neutral-100">
                                <div className="flex items-start gap-6">
                                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                                        <span className="text-3xl">⚠️</span>
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-medium text-neutral-800 mb-4">
                                            Важлива інформація
                                        </h3>
                                        <div className="space-y-4 text-neutral-600 leading-relaxed">
                                            <p>
                                                Якщо ви бажаєте отримати замовлення іншою службою доставки (Укрпошта, Meest, Justin тощо), будь ласка, <strong className="text-neutral-800">повідомте про це менеджера</strong> при оформленні замовлення.
                                            </p>
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                                <p className="text-amber-800 font-medium">
                                                    Зверніть увагу: доставка іншими службами здійснюється виключно за умови <strong>повної передоплати</strong> замовлення.
                                                </p>
                                            </div>
                                            <p>
                                                Наш менеджер розрахує вартість доставки обраною службою та надасть всю необхідну інформацію.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AnimatedSection>
                    </div>
                </section>

                {/* Delivery Process Section */}
                <section className="py-20 lg:py-32 px-6">
                    <div className="max-w-4xl mx-auto">
                        <AnimatedSection className="text-center mb-16">
                            <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                Як це працює
                            </span>
                            <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight">
                                Процес доставки
                            </h2>
                        </AnimatedSection>

                        <div className="space-y-8">
                            {deliverySteps.map((step, index) => (
                                <DeliveryStep
                                    key={index}
                                    number={index + 1}
                                    title={step.title}
                                    description={step.description}
                                    delay={index * 100}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Packaging Section */}
                <section className="py-20 lg:py-32 bg-neutral-50 px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            <AnimatedSection animation="scale">
                                <div className="relative">
                                    <div className="aspect-square bg-gradient-to-br from-brand-gold/20 to-brand-gold/5 rounded-2xl flex items-center justify-center">
                                        <div className="text-brand-gold text-9xl font-light opacity-30">🎁</div>
                                    </div>
                                    <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-gold rounded-xl -z-10" />
                                </div>
                            </AnimatedSection>
                            <div>
                                <AnimatedSection>
                                    <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                        Подарункова упаковка
                                    </span>
                                </AnimatedSection>
                                <AnimatedSection delay={100}>
                                    <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 mb-8 tracking-tight">
                                        Ваше замовлення в надійних руках
                                    </h2>
                                </AnimatedSection>
                                <AnimatedSection delay={200}>
                                    <div className="space-y-6 text-neutral-600 leading-relaxed">
                                        <p>
                                            Кожен виріб ретельно упаковується в фірмову подарункову коробку бренду «Цацки», яка захищає прикрасу під час транспортування.
                                        </p>
                                        <p>
                                            У нас безліч різних варіантів для індивідуалізації вашого замовлення, спитайте менеджера що до особливого пакування. Для додаткового захисту можна додати протиударну упаковку, що гарантує збереження вашого замовлення в ідеальному стані.
                                        </p>
                                        <ul className="space-y-3">
                                            <li className="flex items-center gap-3">
                                                <span className="w-2 h-2 rounded-full bg-brand-gold"></span>
                                                <span>Фірмова подарункова коробка</span>
                                            </li>
                                            <li className="flex items-center gap-3">
                                                <span className="w-2 h-2 rounded-full bg-brand-gold"></span>
                                                <span>Протиударна упаковка (за необхідності)</span>
                                            </li>
                                            <li className="flex items-center gap-3">
                                                <span className="w-2 h-2 rounded-full bg-brand-gold"></span>
                                                <span>Сертифікат якості (або заводський ярлик)</span>
                                            </li>
                                        </ul>
                                    </div>
                                </AnimatedSection>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section className="py-20 lg:py-32 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 px-6 relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl" />
                    </div>

                    <div className="max-w-4xl mx-auto text-center relative z-10">
                        <AnimatedSection>
                            <h2 className="text-3xl lg:text-5xl font-light text-white mb-6 tracking-tight">
                                Маєте питання щодо доставки?
                            </h2>
                        </AnimatedSection>
                        <AnimatedSection delay={100}>
                            <p className="text-white/70 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
                                Зв'яжіться з нами і ми допоможемо обрати найзручніший спосіб доставки для вас.
                            </p>
                        </AnimatedSection>
                        <AnimatedSection delay={200}>
                            <button
                                onClick={() => setContactVisible(true)}
                                className="inline-block btn-animate bg-brand-gold hover:bg-brand-gold-hover text-white px-12 py-5 rounded-sm text-lg font-medium tracking-wide uppercase transition-colors cursor-pointer"
                            >
                                Зв'яжіться зі мною
                            </button>
                        </AnimatedSection>
                    </div>
                </section>
            </main>

            {/* Contact Sidebar */}
            <Sidebar
                className={isMobile ? "w-full" : "w-1/2"}
                visible={contactVisible}
                position="right"
                onHide={() => setContactVisible(false)}
            >
                <ContactMeBlock />
            </Sidebar>

            <Footer containerClassNames="bg-white text-vidrodzhennya" />
        </div>
    );
}

export default Delivery;
